import React, { useMemo, useRef, useState } from 'react';
import { Pagination, Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { styled, Box, useTheme, ButtonBase } from '@material-ui/core';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { Caption } from './Caption';

const Image = styled('img')({
  width: 'auto',
  height: 260,
  display: 'inline',
  maxWidth: '100%',
  margin: 'auto',
  '@media (min-width: 768px)': {
    height: 500,
  },
});

const StyledSwiper = styled(Swiper)(({ theme }) => ({
  '& .MuiFormControl-root': {
    width: '100%',
  },
}));

const PaginationWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '15px',
  width: 'fit-content',
  margin: '0 auto',
  marginTop: '20px',

  '& .swiper-pagination': {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    margin: 0,
    marginTop: '20px',
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },

  '& .swiper-pagination-bullet': {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#979797',
    opacity: 0.3,
  },
  '& .swiper-pagination-bullet.swiper-pagination-bullet-active': {
    width: '12px',
    height: '12px',
    backgroundColor: theme.palette.primary.main,
    opacity: 1,
  },
}));

export const SdmImageSwiper = ({ images }) => {
  const theme = useTheme();
  const swiperRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(1);

  const totalSlides = images.length;

  const imageSlides = useMemo(() => {
    return images.map((el, i) => {
      const { src, alt, caption } = el;
      return (
        <SwiperSlide key={`${src}-${i}`} virtualIndex={i}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ width: '100%' }}
          >
            <Box
              display="flex"
              flexDirection="column"
              width="fit-content"
              justifyContent="center"
              alignItems="center"
            >
              <Image src={src} alt={alt} />
              {caption && <Caption text={caption} textAlign="left" />}
            </Box>
          </Box>
        </SwiperSlide>
      );
    });
  }, [images]);

  const togglePlay = () => {
    if (!swiperRef.current) return;
    if (playing) {
      swiperRef.current.autoplay.stop();
    } else {
      swiperRef.current.autoplay.start();
    }
    setPlaying(!playing);
  };

  return (
    <>
      <StyledSwiper
        modules={[Pagination, Autoplay]}
        pagination={{ el: '.swiper-pagination', clickable: true }}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        slidesPerView={1}
        loop
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper) => {
          setCurrentIndex(swiper.realIndex + 1);
        }}
      >
        {imageSlides}
      </StyledSwiper>

      <PaginationWrapper>
        <Box
          onClick={() => swiperRef.current?.slidePrev()}
          sx={{
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ArrowBackIosIcon
            sx={{ fontSize: 15, color: theme.palette.primary.main }}
          />
        </Box>

        <div className="swiper-pagination" />

        <Box
          sx={{
            fontSize: 14,
            color: theme.palette.primary.main,
            display: { xs: 'block', md: 'none' },
          }}
        >
          {currentIndex} / {totalSlides}
        </Box>

        <ButtonBase onClick={togglePlay}>
          {playing ? (
            <PauseIcon
              sx={{
                fontSize: 15,
                color: {
                  xs: theme.palette.grey[900],
                  md: theme.palette.primary.main,
                },
              }}
            />
          ) : (
            <PlayArrowIcon
              sx={{
                fontSize: 15,
                color: {
                  xs: theme.palette.grey[900],
                  md: theme.palette.primary.main,
                },
              }}
            />
          )}
        </ButtonBase>

        <Box
          onClick={() => swiperRef.current?.slideNext()}
          sx={{
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ArrowForwardIosIcon
            sx={{ fontSize: 15, color: theme.palette.primary.main }}
          />
        </Box>
      </PaginationWrapper>
    </>
  );
};
